import { useUserPersonalData } from '@apis/queries/useUserPersonalData/useUserPersonalData';
import { APP_LINKS, APP_SUB_MENUS, NAVBAR_LINKS } from '@utils/variables';
import { useRouter } from 'next/router';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import ArrowButton from 'src/components/buttons/ArrowButton';
import DotsNavigation from 'src/components/common/DotsNavigation';
import { ISubmenu } from 'src/components/navbar/Sublinks/Sublinks.type';
import HotkeyTooltip from 'src/components/tooltips/HotkeyTooltip';
import { hotkeyTexts } from 'src/components/tooltips/HotkeyTooltip/Hotkey.texts';
import { isOnSubpage, pushURL } from 'src/functions/functions';
import useResponsive from 'src/hooks/contexts/useResponsive';
import useNavigation from 'src/hooks/events/useNavigation';
import useCharacterRouter from 'src/hooks/routers/useCharacterRouter';
import useAppHeight from 'src/hooks/useAppHeight';
import useCurrentPageId from 'src/store/appState/hooks/useCurrentPageId';
import useCurrentSubpage from 'src/store/appState/hooks/useCurrentSubpage';
import Flex from 'src/styles/layouts/Flex';
import { EFlex } from 'src/styles/layouts/Flex/Flex.enum';
import { EDirection } from 'src/styles/theme/utils/enums';
import { StyledDotsContainer, StyledSinglePageContainer } from './SinglePageContainer.styles';
import { ISinglePageContainer } from './SinglePageContainer.type';

// used to prevent spam on click
let rightClicked = false;
// used to prevent spam on click
let leftClicked = false;

const SinglePageContainer: FC<ISinglePageContainer> = ({ children }) => {
	const router = useRouter();

	const [currentPageId] = useCurrentPageId();
	const [{ id: currentSubpageId }] = useCurrentSubpage();

	useAppHeight();

	const { isSuperUser } = useUserPersonalData();
	const { isSmallerThanMd } = useResponsive();
	const { isInCustomizationView } = useCharacterRouter();

	const [isAppReady, setIsAppReady] = useState(false);

	const submenus = useMemo(() => {
		const pageWithSubmenus = NAVBAR_LINKS.find(({ id }) => id === currentPageId);
		return pageWithSubmenus?.submenus || [];
	}, [currentPageId]);

	const leftArrowClicked = useCallback(() => {
		if (isInCustomizationView || leftClicked) {
			return;
		}

		leftClicked = true;

		// get currentPage
		const pageWithSubmenusIndex = NAVBAR_LINKS.findIndex(({ id }) => id === currentPageId);

		if (pageWithSubmenusIndex < 0) {
			leftClicked = false;
			return;
		}

		// get currentPage
		const pageWithSubmenus = NAVBAR_LINKS[pageWithSubmenusIndex];
		const _submenus = pageWithSubmenus?.submenus || [];

		const submenuIndex = _submenus.findIndex(({ id }) => id === currentSubpageId) || 0;

		if (submenuIndex === 0) {
			// check if there is a next page
			if (pageWithSubmenusIndex === 0) {
				leftClicked = false;
				return;
			}

			const nextPage = NAVBAR_LINKS[pageWithSubmenusIndex - 1];

			const nextSubmenus = nextPage?.submenus;

			const nextSubmenu = nextSubmenus?.[nextSubmenus.length - 1];

			if ((nextSubmenu?.id && !nextSubmenu?.isDisabled) || isSuperUser) {
				pushURL(nextPage.href, String(nextSubmenu?.id));
			}

			setTimeout(() => {
				leftClicked = false;
			}, 50);
			return;
		}

		const newSubpage = _submenus[submenuIndex - 1];

		if (!pageWithSubmenus) {
			return;
		}

		if (!newSubpage?.isDisabled || isSuperUser) {
			pushURL(pageWithSubmenus.href, newSubpage?.id);
		}

		// authorize first click but prevent to quick click
		setTimeout(() => {
			leftClicked = false;
		}, 50);
	}, [isInCustomizationView, isSuperUser, currentPageId, currentSubpageId]);

	const rightArrowClicked = useCallback(() => {
		if (isInCustomizationView || rightClicked) {
			return;
		}

		rightClicked = true;

		// get currentPage
		const pageWithSubmenusIndex = NAVBAR_LINKS.findIndex(({ id }) => id === currentPageId);

		let submenuIndex;

		if (currentSubpageId) {
			submenuIndex = submenus.findIndex(({ id }) => id === currentSubpageId);

			// if it's the last submenu of this page
			if (submenuIndex === submenus.length - 1) {
				// check if there is a next page
				if (pageWithSubmenusIndex === NAVBAR_LINKS.length - 1) {
					rightClicked = false;
					return;
				}

				const nextPage = NAVBAR_LINKS[pageWithSubmenusIndex + 1];

				const nextSubmenu = nextPage?.submenus?.[0];

				if ((nextSubmenu?.id && !nextSubmenu?.isDisabled) || isSuperUser) {
					pushURL(nextPage.href, String(nextSubmenu?.id));
				}

				setTimeout(() => {
					rightClicked = false;
				}, 50);
				return;
			} else if (submenuIndex === -1) {
				rightClicked = false;
				return;
			}
		} else {
			submenuIndex = 0;
		}

		const newSubpage = submenus[submenuIndex + 1];

		if (pageWithSubmenusIndex < 0) {
			rightClicked = false;
			return;
		}

		const pageWithSubmenus = NAVBAR_LINKS[pageWithSubmenusIndex];

		if (!newSubpage?.isDisabled || isSuperUser) {
			pushURL(pageWithSubmenus.href, newSubpage?.id);
		}

		// authorize first click but prevent to quick click
		setTimeout(() => {
			rightClicked = false;
		}, 50);
	}, [currentPageId, isSuperUser, currentSubpageId, submenus, isInCustomizationView]);

	const handleDotClick = useCallback(
		(index: number) => {
			if (!submenus || index < 0 || index >= submenus.length || isInCustomizationView) {
				return;
			}

			const newSubmenus = submenus[index];

			// get currentPage
			const pageWithSubmenus = NAVBAR_LINKS.find(({ id }) => id === currentPageId);

			if (!pageWithSubmenus) {
				return;
			}

			if (!newSubmenus?.isDisabled) {
				pushURL(pageWithSubmenus.href, newSubmenus?.id);
			}
		},
		[currentPageId, submenus, isInCustomizationView]
	);

	const currentSubmenusIndex = useMemo(() => {
		return submenus.findIndex(({ id }) => id === currentSubpageId);
	}, [submenus, currentSubpageId]);

	const arrowState = useMemo(
		() => ({
			left:
				currentSubpageId === APP_SUB_MENUS.home.overview.id ||
				currentSubpageId === APP_SUB_MENUS.support.faq.id ||
				currentSubpageId === APP_SUB_MENUS.settings.overview.id ||
				currentSubpageId === APP_SUB_MENUS.staking.protocols.id ||
				router.asPath === APP_LINKS.home.href ||
				router.asPath === APP_LINKS.settings.href,
			right:
				currentSubpageId === APP_SUB_MENUS.support.aboutUs.id ||
				currentSubpageId === APP_SUB_MENUS.journal.questLog.id ||
				currentSubpageId === APP_SUB_MENUS.staking.protocols.id ||
				currentSubpageId === APP_SUB_MENUS.settings.overview.id ||
				router.asPath === APP_LINKS.settings.href
		}),
		[currentSubpageId, router.asPath]
	);

	useEffect(() => {
		if (typeof window === 'undefined' || !isAppReady) {
			document.body.classList.add('is-not-moving');
		} else {
			document.body.classList.remove('is-not-moving');
		}

		const savedSubmenuIndex = window?.['saved-character-subpage'] || 0;

		const newSubmenuIndex = submenus.findIndex((submenu: ISubmenu) => submenu.id === router.query?.s);

		window['saved-character-subpage'] = newSubmenuIndex;

		if (newSubmenuIndex === savedSubmenuIndex) {
			document.body.classList.add('is-not-moving');
		} else {
			document.body.classList.remove('is-not-moving');
		}

		document.body.classList.toggle('is-moving-to-left', newSubmenuIndex < savedSubmenuIndex);
	}, [router.query?.s, isAppReady, submenus]);

	useNavigation(leftArrowClicked, rightArrowClicked, !arrowState.left, !arrowState.right);

	useEffect(() => {
		// add delay before show the first subpage
		setTimeout(() => {
			setIsAppReady(true);
		}, 200);
	}, []);

	return (
		<StyledSinglePageContainer>
			{!isSmallerThanMd ? (
				<HotkeyTooltip inactive={arrowState.left} {...hotkeyTexts.previousPage}>
					<ArrowButton
						arrowClassName='navigation-arrow'
						direction={EDirection.left}
						isDisabled={arrowState.left}
						onClick={leftArrowClicked}
						isHidden={isInCustomizationView}
						data-cy='single-page-left-arrow'
					/>
				</HotkeyTooltip>
			) : null}

			<Flex fullHeight vertical={EFlex.center} horizontal={EFlex.center} smVertical={EFlex.start}>
				<TransitionGroup component={null}>
					<CSSTransition classNames='page-transition' key={!isAppReady ? 'init' : currentSubpageId} timeout={400}>
						{isAppReady ? children : <></>}
					</CSSTransition>
				</TransitionGroup>
			</Flex>

			{!isSmallerThanMd ? (
				<HotkeyTooltip inactive={arrowState.right} {...hotkeyTexts.nextPage}>
					<ArrowButton
						arrowClassName='navigation-arrow'
						direction={EDirection.right}
						isDisabled={arrowState.right}
						onClick={rightArrowClicked}
						isHidden={isInCustomizationView}
						data-cy='single-page-right-arrow'
					/>
				</HotkeyTooltip>
			) : null}

			{isSmallerThanMd ? (
				<StyledDotsContainer
					isHidden={isInCustomizationView}
					addBackground={isOnSubpage([APP_SUB_MENUS.character.forge.id, APP_SUB_MENUS.character.chests.id])}
				>
					<DotsNavigation
						id='dots-navigation-container'
						data-cy='dots-navigation-container'
						currentIndex={!currentSubpageId ? 0 : currentSubmenusIndex}
						onClick={(index) => handleDotClick(index)}
						nbDots={submenus?.length}
						isHidden={isInCustomizationView || submenus.length === 1}
					/>
				</StyledDotsContainer>
			) : null}
		</StyledSinglePageContainer>
	);
};

export { SinglePageContainer };
